import { SelectButton, SelectButtonChangeEvent, SelectButtonProps } from 'primereact/selectbutton';
import { classNames } from 'primereact/utils';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
} from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './SelectButtonInput.module.css';

export type SelectButtonInputProps<T extends FieldValues> = Omit<SelectButtonProps, 'name'> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  selectButtonClassName?: string;
  customIcon?: React.ReactNode;
} & UseControllerProps<T>;

const SelectButtonInput = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  selectButtonClassName,
  clearErrors,
  showError = true,
  customIcon,
  ...props
}: SelectButtonInputProps<T>) => {
  const {
    field: { ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const handleOnChange = (event: SelectButtonChangeEvent) => {
    onChange(event);
    clearErrors?.(name);
  };

  return (
    <div className='relative'>
      <div className={selectButtonClassName}>
        {label && (
          <label className='text-md flex align-items-center gap-2' htmlFor={name}>
            {label}
          </label>
        )}
        <SelectButton
          id={name}
          onChange={handleOnChange}
          {...props}
          {...inputProps}
          className={classNames(className, styles['btn'], {
            'p-invalid': error,
          })}
          invalid={invalid}
        />

        {customIcon}

        {/* Hidden input to enable focus & auto-scroll on error */}
        <input
          readOnly
          className='opacity-0 p-0 m-0 w-0 h-0 absolute pointer-events-none'
          ref={ref}
        />
      </div>
      {showError && invalid && (
        <div className={classNames(styles['error-message-box'])}>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default SelectButtonInput;
